import { useMutation } from '@tanstack/react-query';
import nanoid from 'nanoid';

import container from '../../../../container';
import { currencies, toPercentage } from '../claimsHelpers';
import { useClaimsShopSettings } from '../../ClaimsShopSettingsContext';
import { ClaimContext } from '../types';

type OnChangeFn = (payload: Record<string, unknown>) => void;

export type InvoiceSource = 'manual' | 'import';

const useInvoiceExtraction = (
  claim: Pick<ClaimContext, 'orderItems' | 'isOrderItemsRequired'>,
  onChange: OnChangeFn,
  isOrderItemTaxRate: boolean,
  isCashOnDelivery: boolean,
  shopId: number
) => {
  const shopSettings = useClaimsShopSettings();

  const { isLoading, mutate, isError } = useMutation({
    mutationFn: async (fileId: number) =>
      container.touchpointsApi.getInvoiceExtraction(shopId, fileId, {
        excludeOrderItems: !claim.isOrderItemsRequired,
      }),
    onSuccess: (data) => {
      const { netValue, netValueCurrency, grossValue, orderItems, taxRate } = data;

      if (netValueCurrency && currencies.includes(netValueCurrency)) {
        onChange({ netValueCurrency });
      }

      let isTaxIncluded = false;
      if (isOrderItemTaxRate) {
        isTaxIncluded = orderItems && orderItems.some((item) => item.taxRate);
      } else {
        isTaxIncluded = !!taxRate;
      }

      onChange({
        netValue: isCashOnDelivery ? grossValue : netValue,
        taxRate: taxRate ? toPercentage(taxRate) : null,
        orderItems: orderItems.map((item) => ({
          id: nanoid(),
          description: item.name ? item.name.replace(/\s+/g, ' ').trim().substring(0, 50).split(/,|;/)[0] : '',
          unitPrice: String(item.price || 0),
          quantity: String(Math.floor(item.quantity || 0)),
          taxRate: item.taxRate ? toPercentage(item.taxRate) : null,
        })),
        ...(shopSettings.isDefaultTaxIncluded ? { isTaxIncluded } : {}),
      });
    },
  });

  const extractInvoiceData = (fileId: number, invoiceSource?: InvoiceSource) => {
    const shouldExtractOrderItems =
      shopSettings.isInvoiceExtractionEnabled && !(claim.orderItems.length > 0 && invoiceSource === 'import');

    if (shouldExtractOrderItems) {
      mutate(fileId);
    }
  };

  return {
    isExtractingInvoice: isLoading,
    extractInvoiceData,
    isFailedToExtractInvoice: isError,
  };
};

export default useInvoiceExtraction;
